import { useMemo } from "react";
import {
  useAssignAndGetEvaluationTask,
  useAssignAndGetEvaluationTaskByPhase,
  useGetAssignedEvaluationTask,
  useGetAssignedEvaluationTaskV3,
  type EvaluationTask,
  type EvaluationTaskQuestionV3,
  type EvaluationTaskV3,
  type Question,
} from "@services/evaluationService";
import {
  getReportIssueContextFromTaskV3,
  type ReportIssueContext,
} from "@services/MetaService";

function mapV3QuestionToQuestion(question: EvaluationTaskQuestionV3): Question {
  return {
    question_id: question.question_id,
    question_number: question.question_number,
    min_marks: question.min_marks,
    max_marks: question.max_marks,
    rubrics: question.rubrics,
    previous_evaluation_result: question.previous_evaluation_result,
    current_evaluation: question.current_evaluation,
    student_comment: question.student_comment?.length
      ? question.student_comment
      : null,
  };
}

function mapV3TaskToEvaluationTask(task: EvaluationTaskV3): EvaluationTask {
  return {
    id: task.id ?? "",
    assignmentId: task.assignment_id ?? "",
    is_challenge: false,
    submission_url: task.exam_submission.submission_url ?? "",
    evaluation_group_id: task.evaluation_group_id,
    evaluation_phase_id: task.evaluation_phase_id,
    questions: task.questions.map(mapV3QuestionToQuestion),
  };
}

/**
 * Hook to get and transform assigned evaluation task
 */
export function useAssignedEvaluationTask(enabled: boolean = true) {
  const { data, isLoading, error, refetch } =
    useGetAssignedEvaluationTask(enabled);
  const task = useMemo<EvaluationTask | null>(
    () =>
      data
        ? { ...data, assignmentId: data.assignmentId ?? data.id }
        : null,
    [data]
  );

  const questions = useMemo<Question[]>(() => task?.questions || [], [task]);

  const hasTask = task !== null;
  const hasQuestions = questions.length > 0;

  return {
    task,
    questions,
    taskId: task?.id,
    isChallenge: task?.is_challenge ?? false,
    isLoading,
    error,
    hasTask,
    hasQuestions,
    refetch,
  };
}

/**
 * Hook to assign and get evaluation task for a specific group
 */
export function useAssignEvaluationTask(
  evaluationGroupId?: string,
  enabled: boolean = true
) {
  const { data, isLoading, error, refetch } = useAssignAndGetEvaluationTask(
    evaluationGroupId,
    enabled
  );

  const task = useMemo<EvaluationTask | null>(
    () =>
      data
        ? { ...data, assignmentId: data.assignmentId ?? data.id }
        : null,
    [data]
  );

  const questions = useMemo<Question[]>(() => task?.questions || [], [task]);

  const hasTask = task !== null;
  const hasQuestions = questions.length > 0;

  return {
    task,
    questions,
    taskId: task?.id,
    isChallenge: task?.is_challenge ?? false,
    isLoading,
    error,
    hasTask,
    hasQuestions,
    refetch,
  };
}

/**
 * Hook to assign and get evaluation task for a specific phase (v3)
 */
export function useAssignEvaluationTaskByPhase(
  evaluationPhaseId?: string,
  enabled: boolean = true
) {
  const { data, isLoading, error, refetch } =
    useAssignAndGetEvaluationTaskByPhase(evaluationPhaseId, enabled);

  const task = useMemo<EvaluationTask | null>(
    () => (data ? mapV3TaskToEvaluationTask(data) : null),
    [data]
  );

  const questions = useMemo<Question[]>(() => task?.questions || [], [task]);

  const reportIssueContext = useMemo<ReportIssueContext | null>(
    () => (data ? getReportIssueContextFromTaskV3(data) : null),
    [data]
  );

  const hasTask = task !== null;
  const hasQuestions = questions.length > 0;

  return {
    task,
    questions,
    taskId: task?.id,
    reportIssueContext,
    isChallenge: task?.is_challenge ?? false,
    isLoading,
    error,
    hasTask,
    hasQuestions,
    refetch,
  };
}

/**
 * Hook to get assigned evaluation task (v3) mapped for the evaluation UI
 */
export function useAssignedEvaluationTaskV3(enabled: boolean = true) {
  const { data, isLoading, isFetching, error, refetch } =
    useGetAssignedEvaluationTaskV3(enabled);

  const task = useMemo<EvaluationTask | null>(
    () => (data ? mapV3TaskToEvaluationTask(data) : null),
    [data]
  );

  const questions = useMemo<Question[]>(() => task?.questions || [], [task]);

  const reportIssueContext = useMemo<ReportIssueContext | null>(
    () => (data ? getReportIssueContextFromTaskV3(data) : null),
    [data]
  );

  const hasTask = task !== null;
  const hasQuestions = questions.length > 0;

  return {
    task,
    questions,
    taskId: task?.id,
    reportIssueContext,
    isChallenge: task?.is_challenge ?? false,
    isLoading,
    isFetching,
    error,
    hasTask,
    hasQuestions,
    refetch,
  };
}
